'use client';

import React, { FC, ReactElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslations } from 'next-intl';
import { GameDifficulty } from '@/contexts/games/types';
import { MazeMode, Position, PathSegment, RadialMazeState, VoronoiMazeState } from './types/maze-types';
import { MazeStateManager } from './MazeState';
import { MazeRenderer } from './helpers/mazeRenderer';
import { RadialMazeRenderer } from './helpers/radialMazeRenderer';
import { VoronoiMazeRenderer } from './helpers/voronoiMazeRenderer';
import { generateMazeWithShape, addCurvatureToWalls } from './helpers/mazeGenerator';
import { generateRadialMaze } from './helpers/radialMazeGenerator';
import {
  isPathCrossingWalls,
  isPathCrossingCurvedWalls,
  difficultySettings,
  calculateCanvasSize,
  checkMazeCompletion as isMazeCompleted,
  setupHighDPICanvas
} from './helpers/mazeUtils';
import { isPathCrossingRadialWall } from './helpers/polarUtils';
import type { PolarWall } from './helpers/polarUtils';
import { extractPolarWalls } from './helpers/radialWalls';
import { checkRadialMazeCompletion } from './helpers/radialCompletion';
import { checkVoronoiCompletion } from './helpers/voronoiCompletion';
import { extractVoronoiWalls, isPathCrossingVoronoiWall, type VoronoiWallEdge } from './helpers/voronoiWalls';
import { refitVoronoiState } from './helpers/voronoiFit';
import { ZoomIn, ZoomOut, Shrink } from 'lucide-react';
import { mazeImagePairs } from './assets/mazeImages';

interface MazeCanvasProps {
  difficulty: GameDifficulty;
  lineColor: string;
  mode?: MazeMode;
  /** Externally-supplied Voronoi maze state (for template/image modes) */
  voronoiMazeData?: VoronoiMazeState | null;
  /** Solution path to overlay on the maze (array of region site positions) */
  solutionPath?: number[] | null;
  onRef?: (ref: {
    regenerate: () => void;
    download: () => void;
    getState: () => any;
    getCanvas: () => HTMLCanvasElement | null;
  } | null) => void;
  onComplete?: () => void;
  onPathUpdate?: () => void;
  onGameStart?: () => void;
}

/**
 * Компонент канваса лабиринта - адаптирован для универсальной архитектуры
 */
export const MazeCanvas: FC<MazeCanvasProps> = ({
  difficulty,
  lineColor,
  mode = 'rectangular',
  voronoiMazeData,
  solutionPath,
  onRef,
  onComplete,
  onPathUpdate,
  onGameStart,
}): ReactElement => {
  const t = useTranslations();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const stateManagerRef = useRef<MazeStateManager | null>(null);
  const rendererRef = useRef<MazeRenderer | null>(null);
  const radialRendererRef = useRef<RadialMazeRenderer | null>(null);
  const radialStateRef = useRef<RadialMazeState | null>(null);
  const polarWallsRef = useRef<PolarWall[]>([]);
  const voronoiRendererRef = useRef<VoronoiMazeRenderer | null>(null);
  const voronoiStateRef = useRef<VoronoiMazeState | null>(null);
  const voronoiWallsRef = useRef<VoronoiWallEdge[]>([]);
  /** Pending requestAnimationFrame id for pointer-move throttling */
  const rafIdRef = useRef<number | null>(null);

  const [, forceUpdate] = useState({});
  // Image and template modes both use voronoi rendering
  const isVoronoiMode = useMemo(() => mode === 'template' || mode === 'image', [mode]);

  const [isDrawing, setIsDrawing] = useState(false);

  // ── Zoom / pan (pinch with two fingers, wheel+ctrl, or the corner buttons) ──
  // A dense (expert / photo) maze on a phone has ~12 px cells: without zoom the
  // openings are not visible. The transform sits on the wrapper; pointer→canvas
  // mapping goes through getBoundingClientRect, so drawing stays exact at any
  // zoom. One finger draws; a second finger cancels the stroke and pans/zooms.
  const [view, setView] = useState({ scale: 1, tx: 0, ty: 0 });
  const viewRef = useRef(view);
  viewRef.current = view;
  const pointersRef = useRef(new Map<number, { x: number; y: number }>());
  const gestureRef = useRef<{ dist: number; mid: { x: number; y: number }; view: { scale: number; tx: number; ty: number } } | null>(null);
  const MIN_ZOOM = 1;
  const MAX_ZOOM = 4;
  const clampView = useCallback((v: { scale: number; tx: number; ty: number }) => {
    const scale = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, v.scale));
    if (scale === 1) return { scale: 1, tx: 0, ty: 0 };
    // keep the maze on screen: pan limited to the overflow on each side
    const el = containerRef.current;
    const cw = el?.clientWidth ?? 0;
    const ch = el?.clientHeight ?? 0;
    const maxTx = (cw * (scale - 1)) / 2 + 40;
    const maxTy = (ch * (scale - 1)) / 2 + 40;
    return { scale, tx: Math.max(-maxTx, Math.min(maxTx, v.tx)), ty: Math.max(-maxTy, Math.min(maxTy, v.ty)) };
  }, []);
  const zoomBy = useCallback((factor: number) => {
    setView((v) => clampView({ scale: v.scale * factor, tx: v.tx * factor, ty: v.ty * factor }));
  }, [clampView]);
  const resetView = useCallback(() => setView({ scale: 1, tx: 0, ty: 0 }), []);
  useEffect(() => { resetView(); }, [voronoiMazeData, mode, difficulty, resetView]);
  const [currentPosition, setCurrentPosition] = useState<Position | null>(null);
  const [canvasSize, setCanvasSize] = useState({ width: 800, height: 600 });
  const [displaySize, setDisplaySize] = useState({ width: 800, height: 600 });
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  
  // Текущая пара изображений для лабиринта
  const currentImagePair = mazeImagePairs[0];

  // Конвертация GameDifficulty в старый формат для совместимости
  const getDifficultyKey = (gameDifficulty: GameDifficulty) => {
    const mapping = {
      'easy': 'easy',
      'medium': 'medium',
      'hard': 'hard',
      'expert': 'expert' // expert теперь использует свои настройки с curvature 0.9
    } as const;
    return mapping[gameDifficulty];
  };

  // Track when component is mounted on client
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Обновляем размеры отображения только на клиенте после гидратации
  useEffect(() => {
    if (isMounted) {
      const isMobile = window.innerWidth < 768;
      const maxW = window.innerWidth - (isMobile ? 8 : 32);
      const maxH = window.innerHeight - (isMobile ? 48 : 80);
      const aspect = canvasSize.width / canvasSize.height;
      // Fit canvas proportionally into available space
      let w = Math.min(canvasSize.width, maxW);
      let h = w / aspect;
      if (h > maxH) {
        h = maxH;
        w = h * aspect;
      }
      setDisplaySize({ width: Math.round(w), height: Math.round(h) });
    }
  }, [canvasSize, isMounted]);

  // Стабильная ссылка на функцию генерации лабиринта
  const regenerateMazeRef = useRef<() => void>(() => {});
  
  regenerateMazeRef.current = () => {
    if (!isMounted) return;

    // Voronoi modes (template/image): maze comes from server via SSE.
    // Re-render existing Voronoi state if available.
    if (isVoronoiMode) {
      if (voronoiStateRef.current && voronoiRendererRef.current && canvasRef.current) {
        const vs = voronoiStateRef.current;
        vs.pathSegments = [];
        vs.isCompleted = false;
        setupHighDPICanvas(canvasRef.current, vs.canvasSize.width, vs.canvasSize.height);
        voronoiRendererRef.current.render(vs);
        onPathUpdate?.();
      }
      return;
    }

    const isMobile = window.innerWidth < 768;
    const newCanvasSize = calculateCanvasSize(
      window.innerWidth,
      window.innerHeight,
      isMobile ? 40 : 60, 0
    );

    setCanvasSize(newCanvasSize);

    if (canvasRef.current) {
      setupHighDPICanvas(canvasRef.current, newCanvasSize.width, newCanvasSize.height);
    }

    try {
      if (mode === 'radial') {
        // --- Radial maze generation ---
        const radialState = generateRadialMaze(newCanvasSize, difficulty);
        radialState.isDarkMode = isDarkMode;
        radialStateRef.current = radialState;
        polarWallsRef.current = extractPolarWalls(radialState);

        // Ensure radial renderer exists
        if (canvasRef.current && !radialRendererRef.current) {
          radialRendererRef.current = new RadialMazeRenderer(canvasRef.current);
        }
        radialRendererRef.current?.render(radialState);
        onPathUpdate?.();
      } else {
        // --- Rectangular maze generation ---
        if (!stateManagerRef.current) return;

        radialStateRef.current = null;
        polarWallsRef.current = [];

        const difficultyKey = getDifficultyKey(difficulty);
        const settings = difficultySettings[difficultyKey];

        const cols = Math.floor(newCanvasSize.width / settings.cellSize);
        const rows = Math.floor(newCanvasSize.height / settings.cellSize);

        if (cols <= 0 || rows <= 0) return;

        const result = generateMazeWithShape(
          newCanvasSize.width,
          newCanvasSize.height,
          settings.cellSize,
          {
            pathComplexity: settings.pathComplexity,
            deadEndDensity: settings.deadEndDensity,
            branchingFactor: settings.branchingFactor,
            minPathLength: settings.minPathLength,
            wallRemovalChance: settings.wallRemovalChance,
            entranceZoneSize: settings.entranceZoneSize,
            exitZoneSize: settings.exitZoneSize
          }
        );

        const { maze, start, end, entranceZone, exitZone, wallSegments } = result;

        if (entranceZone && currentImagePair) {
          entranceZone.image = currentImagePair.start;
        }
        if (exitZone && currentImagePair) {
          exitZone.image = currentImagePair.finish;
        }

        stateManagerRef.current.updateState({
          maze,
          rows,
          cols,
          cellSize: settings.cellSize,
          canvasSize: newCanvasSize,
          start,
          end,
          entranceZone,
          exitZone,
          pathSegments: [],
          isCompleted: false,
          wallSegments: wallSegments || [],
          curvature: 0,
          isDarkMode
        });
        onPathUpdate?.();
      }
    } catch (error) {
      console.error('Error generating maze:', error);
    }
  };

  const regenerateMaze = useCallback(() => {
    regenerateMazeRef.current();
  }, []);

  // Инициализация менеджера состояния (rectangular mode)
  useEffect(() => {
    if (!stateManagerRef.current) {
      const difficultyKey = getDifficultyKey(difficulty);
      stateManagerRef.current = new MazeStateManager({
        canvasSize,
        cellSize: difficultySettings[difficultyKey].cellSize,
        isDarkMode
      });

      // Подписываемся на изменения состояния
      const unsubscribe = stateManagerRef.current.subscribe(() => {
        forceUpdate({});
        if (mode !== 'radial' && !isVoronoiMode && rendererRef.current) {
          rendererRef.current.render(stateManagerRef.current!.getState());
        }
      });

      return unsubscribe;
    }
  }, [canvasSize, isDarkMode, difficulty, mode, isVoronoiMode]);

  // Инициализация рендереров
  useEffect(() => {
    if (canvasRef.current) {
      if (!rendererRef.current) {
        rendererRef.current = new MazeRenderer(canvasRef.current);
      }
      if (!radialRendererRef.current) {
        radialRendererRef.current = new RadialMazeRenderer(canvasRef.current);
      }
      if (!voronoiRendererRef.current) {
        voronoiRendererRef.current = new VoronoiMazeRenderer(canvasRef.current);
      }
    }
  }, []);

  /** The box the maze may fill: this component's own container, minus a hair. */
  const containerBox = useCallback((): { w: number; h: number } | null => {
    const el = containerRef.current;
    if (!el) return null;
    const w = el.clientWidth - 4;
    const h = el.clientHeight - 4;
    return w > 60 && h > 60 ? { w, h } : null;
  }, []);

  /** Push a (re)fitted Voronoi state to the canvas: walls, size, DPI, render. */
  const applyVoronoiState = useCallback((next: VoronoiMazeState) => {
    voronoiStateRef.current = next;
    voronoiWallsRef.current = extractVoronoiWalls(next.mazeData);
    setCanvasSize(next.canvasSize);
    if (canvasRef.current) {
      setupHighDPICanvas(canvasRef.current, next.canvasSize.width, next.canvasSize.height);
      if (!voronoiRendererRef.current) voronoiRendererRef.current = new VoronoiMazeRenderer(canvasRef.current);
      voronoiRendererRef.current.render(next);
    }
  }, []);

  // Handle externally-supplied Voronoi maze data (template/image modes). The
  // parent fitted it to a first-guess box; the canvas re-fits it to ITS real
  // container (the mode bar / idle controls may have collapsed since, and on a
  // phone that is most of the height).
  useEffect(() => {
    if (!isVoronoiMode || !voronoiMazeData) return;
    let next: VoronoiMazeState = {
      ...voronoiMazeData,
      pathSegments: [],
      isCompleted: false,
      isDarkMode,
    };
    const box = containerBox();
    if (box) next = refitVoronoiState(next, box.w, box.h);
    applyVoronoiState(next);
    onPathUpdate?.();
  }, [voronoiMazeData, mode, isVoronoiMode, isDarkMode, onPathUpdate, containerBox, applyVoronoiState]);

  // Container resize (rotation, controls collapsing, window resize): re-fit the
  // live state — cells, contour AND the drawn path move together, nothing is lost.
  useEffect(() => {
    if (!isVoronoiMode) return;
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const ro = new ResizeObserver(() => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        const cur = voronoiStateRef.current;
        const box = containerBox();
        if (!cur || !box) return;
        const next = refitVoronoiState(cur, box.w, box.h);
        if (next !== cur) applyVoronoiState(next);
      }, 120);
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
      if (timer) clearTimeout(timer);
    };
  }, [isVoronoiMode, containerBox, applyVoronoiState]);

  // Определяем темный режим по классу `.dark` на <html> (источник истины
  // дизайн-системы: next-themes attribute="class"). НЕ prefers-color-scheme —
  // иначе канвас игнорирует переключатель темы приложения. MutationObserver
  // перерисовывает лабиринт при переключении темы.
  useEffect(() => {
    if (!isMounted) return;

    const root = document.documentElement;
    const readTheme = () => setIsDarkMode(root.classList.contains('dark'));
    readTheme();

    const observer = new MutationObserver(readTheme);
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, [isMounted]);

  // Обновление рендерера при изменении настроек
  useEffect(() => {
    if (isVoronoiMode && voronoiStateRef.current && voronoiRendererRef.current) {
      voronoiStateRef.current.isDarkMode = isDarkMode;
      voronoiRendererRef.current.render(voronoiStateRef.current);
    } else if (mode === 'radial' && radialStateRef.current && radialRendererRef.current) {
      radialStateRef.current.isDarkMode = isDarkMode;
      radialRendererRef.current.render(radialStateRef.current);
    } else if (stateManagerRef.current && rendererRef.current) {
      // Persist the current theme into rectangular state so the renderer
      // (which reads state.isDarkMode) uses it; updateState re-renders via
      // the subscription.
      stateManagerRef.current.updateState({ isDarkMode });
    }
  }, [isDarkMode, lineColor, mode, isVoronoiMode]);

  // Обработка изменения размера окна (только на клиенте)
  useEffect(() => {
    if (!isMounted) return;

    const handleResize = () => {
      regenerateMazeRef.current();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [difficulty, mode, isMounted]);

  // Принудительная перерисовка при смене языка
  useEffect(() => {
    const timer = setTimeout(() => {
      if (canvasRef.current) {
        setupHighDPICanvas(canvasRef.current, canvasSize.width, canvasSize.height);
        if (isVoronoiMode && voronoiStateRef.current && voronoiRendererRef.current) {
          voronoiRendererRef.current.render(voronoiStateRef.current);
        } else if (mode === 'radial' && radialStateRef.current && radialRendererRef.current) {
          radialRendererRef.current.render(radialStateRef.current);
        } else if (stateManagerRef.current && rendererRef.current) {
          rendererRef.current.render(stateManagerRef.current.getState());
        }
      }
    }, 150);

    return () => clearTimeout(timer);
  }, [t, canvasSize, mode, isVoronoiMode]);

  // Функция для получения смещения лабиринта на канвасе
  const getMazeOffset = useCallback(() => {
    if (!stateManagerRef.current) return { offsetX: 0, offsetY: 0 };
    
    const state = stateManagerRef.current.getState();
    const mazeWidth = state.cols * state.cellSize;
    const mazeHeight = state.rows * state.cellSize;
    const offsetX = (state.canvasSize.width - mazeWidth) / 2;
    const offsetY = (state.canvasSize.height - mazeHeight) / 2;
    return { offsetX: Math.max(0, offsetX), offsetY: Math.max(0, offsetY) };
  }, []);

  // Инициализация лабиринта после готовности всех компонентов (только на клиенте)
  useEffect(() => {
    if (isInitialized || !isMounted) return;

    const timer = setTimeout(() => {
      if (canvasRef.current) {
        const canRegenerate = isVoronoiMode
          ? true // voronoi modes render from external voronoiMazeData
          : mode === 'radial'
          ? !!radialRendererRef.current || true // radial renderer created in regenerate
          : !!stateManagerRef.current && !!rendererRef.current;
        if (canRegenerate) {
          regenerateMazeRef.current();
          setIsInitialized(true);
        }
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [isInitialized, isMounted, mode, isVoronoiMode]);

  // Скачивание лабиринта
  const downloadMazeImage = useCallback(() => {
    try {
      let dataUrl: string | undefined;

      if (isVoronoiMode && voronoiStateRef.current && voronoiRendererRef.current) {
        dataUrl = voronoiRendererRef.current.createDownloadImage(voronoiStateRef.current);
      } else if (mode === 'radial' && radialStateRef.current && radialRendererRef.current) {
        dataUrl = radialRendererRef.current.createDownloadImage(radialStateRef.current);
      } else if (stateManagerRef.current && rendererRef.current) {
        const state = stateManagerRef.current.getState();
        dataUrl = rendererRef.current.createDownloadImage(state);
      }

      if (!dataUrl) return;

      const downloadLink = document.createElement('a');
      downloadLink.href = dataUrl;
      downloadLink.download = `maze-${new Date().toISOString().slice(0, 10)}.png`;

      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    } catch (error) {
      console.error('Error downloading maze image:', error);
    }
  }, [mode, isVoronoiMode]);

  // Отдельный эффект для перегенерации при изменении сложности или режима
  useEffect(() => {
    if (isInitialized) {
      regenerateMazeRef.current();
    }
  }, [difficulty, mode, isInitialized]);

  // Методы для получения состояния и канваса
  const getState = useCallback(() => {
    if (isVoronoiMode) {
      return voronoiStateRef.current || null;
    }
    if (mode === 'radial') {
      return radialStateRef.current || null;
    }
    return stateManagerRef.current?.getState() || null;
  }, [mode, isVoronoiMode]);

  const getCanvas = useCallback(() => {
    return canvasRef.current;
  }, []);

  // Предоставляем методы для внешнего управления
  useEffect(() => {
    if (onRef) {
      onRef({
        regenerate: regenerateMaze,
        download: downloadMazeImage,
        getState,
        getCanvas
      });
    }
    return () => {
      if (onRef) onRef(null);
    };
  }, [onRef, regenerateMaze, downloadMazeImage, getState, getCanvas]);

  // Disable Telegram vertical swipes when canvas is active
  useEffect(() => {
    const tg = (window as any).Telegram?.WebApp;
    if (tg?.disableVerticalSwipes) {
      tg.disableVerticalSwipes();
    }
    return () => {
      if (tg?.enableVerticalSwipes) {
        tg.enableVerticalSwipes();
      }
    };
  }, []);

  // Helper: convert pointer event to canvas-space position
  const getCanvasPosition = useCallback((e: React.PointerEvent<HTMLCanvasElement>): Position | null => {
    if (!canvasRef.current) return null;
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvasSize.width / rect.width;
    const scaleY = canvasSize.height / rect.height;

    if (mode === 'radial' || isVoronoiMode) {
      // Radial and Voronoi mazes use full canvas coordinates — no maze offset needed
      return {
        x: (e.clientX - rect.left) * scaleX,
        y: (e.clientY - rect.top) * scaleY,
      };
    }

    const { offsetX, offsetY } = getMazeOffset();
    return {
      x: (e.clientX - rect.left) * scaleX - offsetX,
      y: (e.clientY - rect.top) * scaleY - offsetY,
    };
  }, [canvasSize, mode, isVoronoiMode, getMazeOffset]);

  // Stable ref for solutionPath to avoid re-creating renderCurrentMaze on every path change
  const solutionPathRef = useRef<number[] | null>(null);
  solutionPathRef.current = solutionPath ?? null;

  // Helper: re-render the active maze
  const renderCurrentMaze = useCallback(() => {
    if (isVoronoiMode && voronoiStateRef.current && voronoiRendererRef.current) {
      voronoiRendererRef.current.render(voronoiStateRef.current);
      // Render solution path overlay if provided
      if (solutionPathRef.current && solutionPathRef.current.length >= 2) {
        voronoiRendererRef.current.renderSolutionPath(voronoiStateRef.current, solutionPathRef.current);
      }
    } else if (mode === 'radial' && radialStateRef.current && radialRendererRef.current) {
      radialRendererRef.current.render(radialStateRef.current);
    } else if (stateManagerRef.current && rendererRef.current) {
      rendererRef.current.render(stateManagerRef.current.getState());
    }
  }, [mode, isVoronoiMode]);

  // Re-render when solution path changes (show/hide solution)
  useEffect(() => {
    renderCurrentMaze();
  }, [solutionPath, renderCurrentMaze]);

  // Обработчики рисования
  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!canvasRef.current) return;

    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointersRef.current.size >= 2) {
      // second finger → this is a pinch / pan, not a stroke: drop the stroke the
      // first finger just started and remember the gesture origin
      const pts = [...pointersRef.current.values()];
      const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y) || 1;
      const mid = { x: (pts[0].x + pts[1].x) / 2, y: (pts[0].y + pts[1].y) / 2 };
      gestureRef.current = { dist, mid, view: viewRef.current };
      if (isDrawing) {
        if (isVoronoiMode && voronoiStateRef.current) {
          voronoiStateRef.current.pathSegments = voronoiStateRef.current.pathSegments.slice(0, -1);
        } else if (mode === 'radial' && radialStateRef.current) {
          radialStateRef.current.pathSegments = radialStateRef.current.pathSegments.slice(0, -1);
        }
        setIsDrawing(false);
        setCurrentPosition(null);
        renderCurrentMaze();
      }
      return;
    }

    // Check completion state for the active mode
    if (isVoronoiMode) {
      if (!voronoiStateRef.current || voronoiStateRef.current.isCompleted) return;
    } else if (mode === 'radial') {
      if (!radialStateRef.current || radialStateRef.current.isCompleted) return;
    } else {
      if (!stateManagerRef.current) return;
      const state = stateManagerRef.current.getState();
      if (state.isCompleted) return;
    }

    const position = getCanvasPosition(e);
    if (!position) return;

    setIsDrawing(true);
    setCurrentPosition(position);

    // Запускаем таймер при первом касании
    onGameStart?.();

    const newSegment: PathSegment = { points: [position], color: lineColor };

    if (isVoronoiMode && voronoiStateRef.current) {
      voronoiStateRef.current.pathSegments = [...voronoiStateRef.current.pathSegments, newSegment];
    } else if (mode === 'radial' && radialStateRef.current) {
      radialStateRef.current.pathSegments = [...radialStateRef.current.pathSegments, newSegment];
    } else {
      stateManagerRef.current!.addPathSegment(newSegment);
    }

    onPathUpdate?.();
    renderCurrentMaze();
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (pointersRef.current.has(e.pointerId)) pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const g = gestureRef.current;
    if (g && pointersRef.current.size >= 2) {
      const pts = [...pointersRef.current.values()];
      const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y) || 1;
      const mid = { x: (pts[0].x + pts[1].x) / 2, y: (pts[0].y + pts[1].y) / 2 };
      const scale = g.view.scale * (dist / g.dist);
      setView(clampView({ scale, tx: g.view.tx + (mid.x - g.mid.x), ty: g.view.ty + (mid.y - g.mid.y) }));
      return;
    }
    if (!isDrawing || !currentPosition || !canvasRef.current) return;

    const newPosition = getCanvasPosition(e);
    if (!newPosition) return;

    if (isVoronoiMode) {
      // --- Voronoi wall collision ---
      if (!voronoiStateRef.current || voronoiStateRef.current.isCompleted) return;

      if (isPathCrossingVoronoiWall(currentPosition, newPosition, voronoiWallsRef.current)) {
        return;
      }

      const segments = voronoiStateRef.current.pathSegments;
      if (segments.length > 0) {
        const lastSegment = segments[segments.length - 1];
        lastSegment.points = [...lastSegment.points, newPosition];
        setCurrentPosition(newPosition);
        // Throttle rendering to one repaint per animation frame
        if (rafIdRef.current === null) {
          rafIdRef.current = requestAnimationFrame(() => {
            rafIdRef.current = null;
            renderCurrentMaze();
          });
        }
      }
    } else if (mode === 'radial') {
      // --- Radial wall collision ---
      if (!radialStateRef.current || radialStateRef.current.isCompleted) return;

      const walls = polarWallsRef.current;
      for (const wall of walls) {
        if (isPathCrossingRadialWall(currentPosition, newPosition, wall)) {
          return;
        }
      }

      // Update last segment
      const segments = radialStateRef.current.pathSegments;
      if (segments.length > 0) {
        const lastSegment = segments[segments.length - 1];
        lastSegment.points = [...lastSegment.points, newPosition];
        setCurrentPosition(newPosition);
        if (rafIdRef.current === null) {
          rafIdRef.current = requestAnimationFrame(() => {
            rafIdRef.current = null;
            renderCurrentMaze();
          });
        }
      }
    } else {
      // --- Rectangular wall collision ---
      if (!stateManagerRef.current) return;
      const state = stateManagerRef.current.getState();
      if (state.isCompleted) return;

      const crossesWalls = state.wallSegments && state.wallSegments.length > 0
        ? isPathCrossingCurvedWalls(currentPosition, newPosition, state.wallSegments)
        : isPathCrossingWalls(currentPosition, newPosition, state.maze, state.cellSize);

      if (crossesWalls) return;

      const segments = state.pathSegments;
      if (segments.length > 0) {
        const lastSegment = segments[segments.length - 1];
        const updatedPoints = [...lastSegment.points, newPosition];
        stateManagerRef.current.updateLastPathSegment(updatedPoints);
        setCurrentPosition(newPosition);
        if (rafIdRef.current === null) {
          rafIdRef.current = requestAnimationFrame(() => {
            rafIdRef.current = null;
            renderCurrentMaze();
          });
        }
      }
    }
  };

  const handlePointerUpOrLeave = (e?: React.PointerEvent<HTMLCanvasElement>) => {
    if (e) pointersRef.current.delete(e.pointerId);
    else pointersRef.current.clear();
    if (pointersRef.current.size < 2) gestureRef.current = null;
    if (!isDrawing) return;

    // Flush any pending rAF so the final path state is rendered
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
      renderCurrentMaze();
    }

    setIsDrawing(false);
    setCurrentPosition(null);

    if (isVoronoiMode) {
      // --- Voronoi completion check ---
      const vs = voronoiStateRef.current;
      if (vs && vs.pathSegments.length > 0 && !vs.isCompleted) {
        const complete = checkVoronoiCompletion(vs.pathSegments, vs.mazeData);
        if (complete) {
          vs.isCompleted = true;
          renderCurrentMaze();
          onComplete?.();
        }
      }
    } else if (mode === 'radial') {
      // --- Radial completion check ---
      const rs = radialStateRef.current;
      if (rs && rs.pathSegments.length > 0 && !rs.isCompleted) {
        if (checkRadialMazeCompletion(rs.pathSegments, rs)) {
          rs.isCompleted = true;
          renderCurrentMaze();
          onComplete?.();
        }
      }
    } else if (stateManagerRef.current) {
      // --- Rectangular completion check ---
      const state = stateManagerRef.current.getState();

      if (state.pathSegments.length > 0 && !state.isCompleted) {
        const completed = isMazeCompleted(
          state.pathSegments,
          state.entranceZone,
          state.exitZone,
          state.cellSize,
          state.maze
        );

        if (completed) {
          stateManagerRef.current.updateState({ isCompleted: true });
          onComplete?.();
        }
      }
    }
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full flex items-center justify-center overflow-hidden bg-background select-none"
      style={{ touchAction: 'none', WebkitTouchCallout: 'none', userSelect: 'none' }}
    >
      <div
        className="relative flex items-center justify-center max-w-full"
        style={{ transform: `translate(${view.tx}px, ${view.ty}px) scale(${view.scale})`, transformOrigin: 'center center', willChange: 'transform' }}
      >
        <canvas
          ref={canvasRef}
          width={canvasSize.width}
          height={canvasSize.height}
          className="bg-background"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUpOrLeave}
          onPointerCancel={handlePointerUpOrLeave}
          onPointerLeave={handlePointerUpOrLeave}
          onWheel={(e) => { if (e.ctrlKey || e.metaKey) { e.preventDefault(); zoomBy(e.deltaY < 0 ? 1.15 : 1 / 1.15); } }}
          style={{
            width: `${displaySize.width}px`,
            height: 'auto',
            aspectRatio: `${displaySize.width} / ${displaySize.height}`,
            maxWidth: '100%',
            maxHeight: '100%',
            touchAction: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none',
            userSelect: 'none'
          }} 
        />
      </div>
      {/* Zoom controls — pinch / ctrl+wheel do the same; reset appears once zoomed */}
      <div className="absolute bottom-2 right-2 z-5 flex flex-col gap-1" data-testid="maze-zoom-controls">
        <button type="button" onClick={() => zoomBy(1.3)} aria-label="+" data-testid="maze-zoom-in"
          className="flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-card/90 text-foreground shadow-xs backdrop-blur-sm hover:bg-muted disabled:opacity-40"
          disabled={view.scale >= MAX_ZOOM}>
          <ZoomIn className="h-4 w-4" />
        </button>
        <button type="button" onClick={() => zoomBy(1 / 1.3)} aria-label="−" data-testid="maze-zoom-out"
          className="flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-card/90 text-foreground shadow-xs backdrop-blur-sm hover:bg-muted disabled:opacity-40"
          disabled={view.scale <= MIN_ZOOM}>
          <ZoomOut className="h-4 w-4" />
        </button>
        {view.scale > 1 && (
          <button type="button" onClick={resetView} aria-label="reset zoom" data-testid="maze-zoom-reset"
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-card/90 text-foreground shadow-xs backdrop-blur-sm hover:bg-muted">
            <Shrink className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
};
